import { DateTime } from 'luxon';
import {
  FILENAME_URI,
  FILESIZE_URI,
  UPLOADED_ON_URI,
  FILE_SOURCE_URI
} from './uris';
import { makeKeyValuePair } from './attachmentUtil';
import { onLoadLink } from './fileUploader';
import {
  groupFilesByBatchSize,
  uploadFileBatchesInParallel
} from './fileBatchUtils';

const isFileUpload = files =>
  Array.isArray(files) && files.every(f => f.source === FILE_SOURCE_URI);

const toUserFacingError = err => {
  if (err?.extensions?.code === 'BAD_USER_INPUT') return err.message;
  if (err?.networkError?.statusCode === 413)
    return 'Total upload size exceeded. Please upload fewer files at a time.';

  return 'An error occurred while trying to upload files.';
};

export const buildUploadDataV2 = ({
  attachToUri,
  base64Content,
  mimeType,
  fileName,
  fileSize,
  uploadedOn,
  customMetadataFn
}) => ({
  base64Content,
  mimeType,
  keyValues: [
    makeKeyValuePair(FILENAME_URI, fileName),
    makeKeyValuePair(FILESIZE_URI, fileSize),
    makeKeyValuePair(UPLOADED_ON_URI, uploadedOn),
    ...(customMetadataFn ? customMetadataFn({ attachToUri }) : [])
  ]
});

export const onLoadFileV2 = ({
  files,
  onDone,
  setError,
  attachToUri,
  customMetadataFn,
  addAttachment,
  refetch,
  _DateTime = DateTime,
  _maxBytesPerBatch
}) => async () => {
  const uploadedOn = _DateTime.local().toString();

  const uploadFiles = files.map(f =>
    buildUploadDataV2({
      attachToUri,
      base64Content: f.base64Content,
      mimeType: f.mimeType,
      fileName: f.filename,
      fileSize: String(f.size),
      uploadedOn,
      customMetadataFn
    })
  );

  const fileBatches = groupFilesByBatchSize(uploadFiles, _maxBytesPerBatch);
  const uploadError = await uploadFileBatchesInParallel(
    fileBatches,
    addAttachment,
    attachToUri
  );

  if (uploadError) setError(toUserFacingError(uploadError));

  onDone();

  if (!uploadError) refetch?.();
};

export const uploadFileV2 = ({
  addAttachment,
  attachToUri,
  onDone,
  setError,
  linkAttachmentsToObject,
  customMetadataFn,
  refetch
}) => files =>
  isFileUpload(files)
    ? onLoadFileV2({
        addAttachment,
        files,
        attachToUri,
        onDone,
        setError,
        customMetadataFn,
        refetch
      })
    : onLoadLink({ file: files[0], onDone, linkAttachmentsToObject });
